import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { ChevronDown, ChevronUp, ThumbsUp, AlertTriangle, Sparkles } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { RecommendationsSection } from './RecommendationsSection';
import { RiskAlertsSection } from './RiskAlertsSection';

export interface SummaryStrength {
  name: string;
  score: number;
  detail?: string;
}

export interface SummaryWeakness {
  name: string;
  score: number;
  detail?: string;
}

interface SummaryCardProps {
  companyName?: string;
  finalScore?: number;
  ventureLabel?: string;
  strengths: SummaryStrength[];
  weaknesses: SummaryWeakness[];
  recommendations: string[];
  risks: Array<{
    category: string;
    description: string;
    severity: string;
    mitigation?: string;
  }>;
  /**
   * Optional LLM-generated narrative override. When present, overall/strengths/
   * weaknesses paragraphs are sourced from the LLM instead of templates.
   */
  narrative?: {
    overall?: string;
    strengths?: string;
    weaknesses?: string;
  };
}

// ─── Narrative builders ─────────────────────────────────────────────────────

function pickFirstSentence(text?: string): string {
  if (!text) return '';
  const trimmed = text.trim();
  const firstStop = trimmed.search(/[.!?]\s/);
  if (firstStop === -1) return trimmed;
  return trimmed.slice(0, firstStop + 1);
}

function buildOverallNarrative(
  score: number | undefined,
  label: string | undefined,
  subject: string,
  strengthsCount: number,
  weaknessCount: number,
): string {
  if (score === undefined || score === 0) {
    return `${subject} has not yet generated a complete score. Provide more data to receive a full investment readiness assessment.`;
  }
  const tier = label ? label.charAt(0).toUpperCase() + label.slice(1) : 'rated';
  const intro = `${subject} scores ${score}/100, placing it in the ${tier} tier.`;
  if (strengthsCount > 0 && weaknessCount > 0) {
    return `${intro} The venture shows clear strengths anchored by its top dimensions, while specific gaps remain that should be addressed to unlock further investment readiness.`;
  }
  if (strengthsCount > 0) {
    return `${intro} The venture demonstrates broad strengths across most dimensions with no critical gaps flagged at this stage.`;
  }
  if (weaknessCount > 0) {
    return `${intro} Several dimensions are underweight relative to the readiness threshold and should be prioritized for improvement.`;
  }
  return intro;
}

function buildStrengthsNarrative(items: SummaryStrength[]): string {
  if (items.length === 0) return 'No standout strengths identified yet — provide more data to surface what works best.';

  const sentences: string[] = [];
  items.forEach((item, idx) => {
    const detail = pickFirstSentence(item.detail) || `scores well at ${item.score}.`;
    if (idx === 0) {
      sentences.push(`${item.name} (${item.score}) leads — ${detail.charAt(0).toLowerCase() + detail.slice(1)}`);
    } else {
      sentences.push(`${item.name} (${item.score}) also performs strongly: ${detail.charAt(0).toLowerCase() + detail.slice(1)}`);
    }
  });
  return sentences.join(' ');
}

function buildWeaknessesNarrative(items: SummaryWeakness[]): string {
  if (items.length === 0) return 'No critical weaknesses flagged. Continue strengthening evidence and traction to push the score higher.';

  const sentences: string[] = [];
  items.forEach((item, idx) => {
    const detail = pickFirstSentence(item.detail) || `is below the readiness threshold at ${item.score}.`;
    if (idx === 0) {
      sentences.push(`${item.name} (${item.score}) is the priority area — ${detail.charAt(0).toLowerCase() + detail.slice(1)}`);
    } else {
      sentences.push(`${item.name} (${item.score}) also needs attention: ${detail.charAt(0).toLowerCase() + detail.slice(1)}`);
    }
  });
  return sentences.join(' ');
}

// ─── Collapsible sub-card ───────────────────────────────────────────────────

interface SubCardProps {
  title: string;
  count: number;
  Icon: typeof ThumbsUp;
  iconColor: string;
  iconBg: string;
  bgColor: string;
  borderColor: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSubCard({
  title,
  count,
  Icon,
  iconColor,
  iconBg,
  bgColor,
  borderColor,
  children,
  defaultOpen = false,
}: SubCardProps) {
  const colors = useThemeColors();
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <View
      style={{
        backgroundColor: bgColor,
        borderWidth: 1,
        borderColor: borderColor,
        borderRadius: 12,
        overflow: 'hidden',
        marginTop: 12,
      }}
    >
      <Pressable
        onPress={() => setOpen((v) => !v)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 12,
          paddingVertical: 10,
          gap: 10,
        }}
        accessibilityRole="button"
        accessibilityLabel={`${title}, ${count}, ${open ? 'collapse' : 'expand'}`}
      >
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            backgroundColor: iconBg,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={14} color={iconColor} />
        </View>
        <Text
          style={{
            flex: 1,
            color: colors.text.primary,
            fontSize: 13,
            fontWeight: '700',
          }}
          numberOfLines={1}
        >
          {title}
        </Text>
        <View
          style={{
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderWidth: 1,
            borderColor: borderColor,
            borderRadius: 999,
            paddingHorizontal: 8,
            paddingVertical: 2,
          }}
        >
          <Text
            style={{ color: iconColor, fontSize: 11, fontWeight: '700' }}
            numberOfLines={1}
          >
            {count}
          </Text>
        </View>
        <Chevron size={16} color={iconColor} />
      </Pressable>
      {open && (
        <View style={{ paddingHorizontal: 12, paddingBottom: 12 }}>
          {children}
        </View>
      )}
    </View>
  );
}

// ─── Section paragraph ──────────────────────────────────────────────────────

function NarrativeSection({
  label,
  body,
  Icon,
  iconColor,
  colors,
}: {
  label: string;
  body: string;
  Icon: typeof ThumbsUp;
  iconColor: string;
  colors: any;
}) {
  return (
    <View style={{ marginBottom: 12 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Icon size={13} color={iconColor} />
        <Text
          style={{
            color: colors.text.tertiary,
            fontSize: 9,
            fontWeight: '700',
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Text>
      </View>
      <Text
        style={{
          color: colors.text.primary,
          fontSize: 13,
          lineHeight: 19,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function SummaryCard({
  companyName,
  finalScore,
  ventureLabel,
  strengths,
  weaknesses,
  recommendations,
  risks,
  narrative,
}: SummaryCardProps) {
  const colors = useThemeColors();

  const subject = companyName ?? 'This venture';
  // LLM narrative takes precedence; fall back to deterministic template otherwise.
  const overallNarrative =
    narrative?.overall?.trim() ||
    buildOverallNarrative(finalScore, ventureLabel, subject, strengths.length, weaknesses.length);
  const strengthsNarrative =
    narrative?.strengths?.trim() || buildStrengthsNarrative(strengths);
  const weaknessesNarrative =
    narrative?.weaknesses?.trim() || buildWeaknessesNarrative(weaknesses);

  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <NarrativeSection
        label="Overall"
        body={overallNarrative}
        Icon={Sparkles}
        iconColor={colors.primary[600]}
        colors={colors}
      />
      <NarrativeSection
        label="Strengths"
        body={strengthsNarrative}
        Icon={ThumbsUp}
        iconColor={colors.success}
        colors={colors}
      />
      <NarrativeSection
        label="Weaknesses"
        body={weaknessesNarrative}
        Icon={AlertTriangle}
        iconColor={colors.warning}
        colors={colors}
      />

      {recommendations.length > 0 && (
        <CollapsibleSubCard
          title="Recommendations"
          count={recommendations.length}
          Icon={ThumbsUp}
          iconColor={colors.success}
          iconBg="rgba(255,255,255,0.6)"
          bgColor={colors.traffic.greenLight}
          borderColor={`${colors.success}33`}
        >
          <RecommendationsSection recommendations={recommendations} alwaysExpanded />
        </CollapsibleSubCard>
      )}

      {risks.length > 0 && (
        <CollapsibleSubCard
          title="Risk Alerts"
          count={risks.length}
          Icon={AlertTriangle}
          iconColor={colors.error}
          iconBg="rgba(255,255,255,0.6)"
          bgColor={colors.red.light}
          borderColor={colors.red.muted}
        >
          <RiskAlertsSection
            alwaysExpanded
            risks={risks.map(({ category, description, severity }) => ({
              category,
              description,
              severity,
            }))}
          />
        </CollapsibleSubCard>
      )}
    </View>
  );
}
